/**
 * Service for interacting with the Outbound Truck Scheduling API
 */
const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Get all outbound truck schedules with optional filtering
 * @param {Object} filters Optional filters including status, start_date, end_date, etc.
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const getAllOutboundTrucks = async (filters = {}) => {
  const token = localStorage.getItem('token');
  
  // Build query string from filters
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value) {
      queryParams.append(key, value);
    }
  });
  
  const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
  
  const response = await fetch(`${API_URL}/outbound-trucks${queryString}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch outbound trucks: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Get an outbound truck schedule by ID
 * @param {string} id The ID of the outbound truck to retrieve
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const getOutboundTruckById = async (id) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/outbound-trucks/${id}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    throw new Error(`Failed to fetch outbound truck: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Create a new outbound truck schedule
 * @param {Object} truckData The data for the new outbound truck schedule
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const createOutboundTruck = async (truckData) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/outbound-trucks`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(truckData)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to create outbound truck: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Update an existing outbound truck schedule
 * @param {string} id The ID of the outbound truck to update
 * @param {Object} truckData The updated data for the outbound truck
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const updateOutboundTruck = async (id, truckData) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/outbound-trucks/${id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(truckData)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to update outbound truck: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Delete an outbound truck schedule
 * @param {string} id The ID of the outbound truck to delete
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const deleteOutboundTruck = async (id) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${API_URL}/outbound-trucks/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to delete outbound truck: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Mark a truck as staged
 * @param {string} id The ID of the outbound truck to mark as staged
 * @param {Object} stageData Optional data including staged_at timestamp
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const markTruckAsStaged = async (id, stageData = {}) => {
  const token = localStorage.getItem('token');
  
  // Ensure the staged_at timestamp is included
  if (!stageData.staged_at) {
    stageData.staged_at = new Date().toISOString();
  }
  
  const response = await fetch(`${API_URL}/outbound-trucks/${id}/stage`, {
    method: 'PATCH', 
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(stageData)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to mark truck as staged: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Mark a truck as loaded
 * @param {string} id The ID of the outbound truck to mark as loaded
 * @param {Object} loadData Optional data including loaded_at timestamp
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const markTruckAsLoaded = async (id, loadData = {}) => {
  const token = localStorage.getItem('token');
  
  // Ensure the loaded_at timestamp is included
  if (!loadData.loaded_at) {
    loadData.loaded_at = new Date().toISOString();
  }
  
  const response = await fetch(`${API_URL}/outbound-trucks/${id}/load`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(loadData)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to mark truck as loaded: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Mark a truck as shipped
 * @param {string} id The ID of the outbound truck to mark as shipped
 * @param {Object} shipData Optional data including shipped_at, conforming, shipper_notes
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const markTruckAsShipped = async (id, shipData = {}) => {
  const token = localStorage.getItem('token');
  
  // Ensure the shipped_at timestamp is included
  if (!shipData.shipped_at) {
    shipData.shipped_at = new Date().toISOString();
  }
  
  const response = await fetch(`${API_URL}/outbound-trucks/${id}/ship`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(shipData)
  });
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to mark truck as shipped: ${response.status} ${response.statusText}`);
  }
  
  return response.json();
};

/**
 * Mark a truck as completed
 * @param {string} id The ID of the outbound truck to mark as completed
 * @param {Object} completeData Optional data including completed_at timestamp
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const markTruckAsCompleted = async (id, completeData = {}) => {
  const token = localStorage.getItem('token');

  // Ensure the completed_at timestamp is included
  if (!completeData.completed_at) {
    completeData.completed_at = new Date().toISOString();
  }

  // Handle BOL file upload if present
  if (completeData.bolFile) {
    const formData = new FormData();

    // Append the BOL file
    formData.append('bolFile', completeData.bolFile);

    // Append other data as JSON
    const dataWithoutFile = { ...completeData };
    delete dataWithoutFile.bolFile;
    formData.append('data', JSON.stringify(dataWithoutFile));

    const response = await fetch(`${API_URL}/outbound-trucks/${id}/complete-with-bol`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        // Don't set Content-Type when using FormData, it will automatically set the correct multipart/form-data
      },
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to mark truck as completed: ${response.status} ${response.statusText}`);
    }

    return response.json();
  }

  // Regular completion without BOL file
  const response = await fetch(`${API_URL}/outbound-trucks/${id}/complete`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(completeData)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to mark truck as completed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

/**
 * Update the status of an outbound truck
 * @param {string} id The ID of the outbound truck
 * @param {string} status The new status
 * @param {Object} additionalData Additional data for the status update
 * @returns {Promise<Object>} Promise that resolves to the API response
 */
export const updateOutboundTruckStatus = async (id, status, additionalData = {}) => {
  console.log(`updateOutboundTruckStatus - Updating truck ${id} to status: ${status}`);

  // Check for BOL file to use special file upload endpoint
  if (status === 'Completed' && additionalData.bolFile) {
    console.log('Using special BOL file upload for completing truck');
    // We need to handle this case with direct FormData upload
    return markTruckAsCompletedWithBOL(id, additionalData);
  }

  // Use dedicated endpoints for each status change instead of general update
  switch (status) {
    case 'Staged':
      return markTruckAsStaged(id, additionalData);
    case 'Loaded':
      return markTruckAsLoaded(id, additionalData);
    case 'Shipped':
      return markTruckAsShipped(id, additionalData);
    case 'Completed':
      return markTruckAsCompleted(id, additionalData);
    case 'Cancelled':
      // For cancelled, use general update since it can happen from any status
      const cancelData = {
        status,
        cancelled_at: new Date().toISOString(),
        ...additionalData
      };
      return updateOutboundTruck(id, cancelData);
    default:
      // For any other statuses, fall back to direct update
      const statusData = {
        status,
        ...additionalData
      };
      return updateOutboundTruck(id, statusData);
  }
};

/**
 * Mark a truck as completed with BOL file upload
 * @param {string} id - The truck ID
 * @param {Object} data - The data including BOL file
 * @returns {Promise<Object>} API response
 */
export const markTruckAsCompletedWithBOL = async (id, data = {}) => {
  const token = localStorage.getItem('token');

  // Create a FormData object to handle file upload
  const formData = new FormData();

  // Append the BOL file if present
  if (data.bolFile) {
    formData.append('bolFile', data.bolFile);
  }

  // Set completed_at timestamp
  const completed_at = data.completed_at || new Date().toISOString();

  // Add other data as JSON
  const dataWithoutFile = { ...data, completed_at };
  delete dataWithoutFile.bolFile;
  formData.append('data', JSON.stringify(dataWithoutFile));

  console.log('markTruckAsCompletedWithBOL - Sending FormData with BOL file');

  const response = await fetch(`${API_URL}/outbound-trucks/${id}/complete-with-bol`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`
      // Don't set Content-Type, it will be automatically set with boundary for FormData
    },
    body: formData
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `Failed to mark truck as completed: ${response.status} ${response.statusText}`);
  }

  return response.json();
};

// Export the service functions
const outboundTruckService = {
  getAllOutboundTrucks,
  getOutboundTruckById,
  createOutboundTruck,
  updateOutboundTruck,
  deleteOutboundTruck,
  updateOutboundTruckStatus,
  // Add new dedicated status functions to exports
  markTruckAsStaged,
  markTruckAsLoaded,
  markTruckAsShipped,
  markTruckAsCompleted,
  markTruckAsCompletedWithBOL
};

export default outboundTruckService;